/* eslint-disable react/require-default-props */

import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import iconIA from 'assets/images/icone-ia-gradiente.svg';
import Loader from 'components/atoms/loader';
import SectionCard from 'components/organisms/section_card';
import { ApplicationState } from 'store';
import { FeatureType, ManagementState } from 'store/modules/management_store/types';
import { useSpeech } from 'hooks/useSpeech';

interface BiddingOverviewProps {
  onClose: () => void;
  onNavigate: (feature: FeatureType) => void;
}

const BiddingOverview = ({ onClose, onNavigate }: BiddingOverviewProps) => {
  const { speak, stop, isSpeaking, isLoading } = useSpeech();
  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const buildOverviewText = () => {
    const overview = management?.overview;

    if (!overview) return '';

    let text = '';
    if (overview.situacao_atual) {
      text += `Situação atual: ${overview.situacao_atual}. `;
    }
    if (overview.andamentos?.length) {
      text += 'Andamentos: ';
      overview.andamentos.forEach((item) => {
        text += `${item.title}. `;
      });
    }
    if (overview.tasks) {
      text += `Suas tarefas: ${overview.tasks.suas.atrasadas} atrasadas, `;
      text += `${overview.tasks.suas.hoje} para hoje, `;
      text += `${overview.tasks.suas.concluidas} concluídas. `;
    }

    return text;
  };
  const handleSpeak = async () => {
    if (isSpeaking) {
      stop();
      return;
    }

    const text = buildOverviewText();
    await speak(text);
  };

  const TaskCount = ({
    label,
    data,
    variant,
  }: {
    label: string;
    data?: { count: number; titles: string[] };
    variant: string;
  }) => {
    const count = data?.count || 0;
    const titles = data?.titles || [];

    return (
      <div className="mt-3">
        <div className="d-flex align-items-center">
          <span className={`badge badge-${variant} mr-2 badge-overview`}>&nbsp;</span>
          <strong>
            {count} {label}
          </strong>
        </div>

        {titles.length > 0 && (
          <ul className="mt-2 mb-0 pl-4" style={{ listStyleType: 'disc' }}>
            {titles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const AnnotationSection = ({ label, items }: { label: string; items?: string[] }) => {
    if (!items?.length) return null;
    return (
      <>
        <strong className="mt-3 d-block">{label}</strong>
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <>
      <div className="overview-backdrop" />
      <div className="overview-panel">
        <div className="overview-header d-flex justify-content-between align-items-start p-3">
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <img alt="Ícone IA" src={iconIA} height="25" />
              <strong className="ml-2">Visão Geral</strong>
            </div>
            <small className="text-muted">Resumo criado por IA. Verifique as informações.</small>
          </div>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary btn-speak mr-2"
              onClick={handleSpeak}
            >
              <span className="span-speak d-flex align-items-center">
                {' '}
                <i className="material-icons mr-1">{isSpeaking ? 'stop' : 'volume_up'}</i>
                {isLoading ? 'Carregando...' : isSpeaking ? 'Parar' : 'Ler em voz alta'}
              </span>
            </button>
            <button aria-label="Close" className="close" type="button" onClick={onClose}>
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        {request?.get_bidding_overview?.loading ? (
          <Loader />
        ) : (
          <div className="overview-content">
            <SectionCard title="Andamentos da licitação" icon="description">
              <div className="timeline">
                {management?.overview?.andamentos.map((item) => (
                  <div key={item.id} className="timeline-item">
                    <div className="timeline-date">
                      {moment(item?.deadline).format('DD/MM/YYYY, HH:mm')}
                    </div>
                    <div className="timeline-content">{item.title}</div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-outline-primary mt-3"
                onClick={() => onNavigate('attachments')}
              >
                Ver andamentos
              </button>
            </SectionCard>
            <SectionCard title="Tarefas" icon="description">
              <div className="row">
                <div className="col-md-6">
                  <h5 className="mb-3">Suas tarefas</h5>
                  <TaskCount
                    label="Atrasadas"
                    data={management?.overview?.tasks?.suas?.atrasadas}
                    variant="danger"
                  />
                  <TaskCount
                    label="para hoje"
                    data={management?.overview?.tasks?.suas?.hoje}
                    variant="warning"
                  />
                  <TaskCount
                    label="concluídas"
                    data={management?.overview?.tasks?.suas?.concluidas}
                    variant="success"
                  />
                </div>
                <div className="col-md-6">
                  <h5 className="mb-3">Equipe</h5>
                  <TaskCount
                    label="Atrasadas"
                    data={management?.overview?.tasks?.equipe?.atrasadas}
                    variant="danger"
                  />
                  <TaskCount
                    label="para hoje"
                    data={management?.overview?.tasks?.equipe?.hoje}
                    variant="warning"
                  />
                  <TaskCount
                    label="concluídas"
                    data={management?.overview?.tasks?.equipe?.concluidas}
                    variant="success"
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary mt-3"
                onClick={() => onNavigate('tasks')}
              >
                Ver tarefas
              </button>
            </SectionCard>
            <SectionCard title="Anotações" icon="description">
              <p>As anotações feitas nessa licitação são referentes à:</p>
              <AnnotationSection
                label="Pontos de atenção"
                items={management?.overview?.anotacoes?.pontos_atencao}
              />
              <AnnotationSection
                label="Decisões internas"
                items={management?.overview?.anotacoes?.decisoes}
              />
              <AnnotationSection
                label="Pendências"
                items={management?.overview?.anotacoes?.pendencias}
              />
              <button
                type="button"
                className="btn btn-outline-primary mt-3"
                onClick={() => onNavigate('comments')}
              >
                Ver anotações
              </button>
            </SectionCard>
            <SectionCard title="Principais mensagens do chat" icon="description">
              {management?.overview?.chat_resumo && (
                <p className="chat-resumo mb-3">{management.overview.chat_resumo}</p>
              )}
              <div className="timeline">
                {(management?.overview?.chat_principais || []).map((item) => (
                  <div key={`${item?.data}-${item?.mensagem}`} className="timeline-item">
                    <div className="timeline-date">
                      {moment(item?.data, 'DD/MM/YYYY HH:mm').format('DD/MM/YYYY [às] HH:mm')}
                    </div>
                    <div className="timeline-content">{item?.mensagem}</div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-outline-primary mt-3"
                onClick={() => onNavigate('monitoring')}
              >
                Ver mensagens
              </button>
            </SectionCard>
          </div>
        )}
      </div>
    </>
  );
};

export default BiddingOverview;
